import React from "react";
import { createPortal } from "react-dom";
import { X, Scissors, Box, Layers, Compass, MoveVertical, Ruler, FileBox, CheckCircle2, XCircle } from "lucide-react";
import { colors } from "../theme";

const ACCENT = colors.brand;
const GOOD = "#34A853";
const BAD = "#FF3B30";
const INK = "#1b2733";
const GRID = "#cfd8e0";

// ---- small inline diagrams (no external assets) ----
const DiaSimplify = () => (
  <svg viewBox="0 0 260 90" width="100%" height="88" role="img" aria-label="Simplify detailed model to massing">
    {/* detailed */}
    <g transform="translate(6,8)">
      <rect x="0" y="20" width="70" height="52" fill="#eef2f6" stroke={GRID} />
      {[8, 20, 32, 44, 56].map((x) => <line key={x} x1={x} y1="20" x2={x} y2="72" stroke={GRID} />)}
      {[32, 44, 56].map((y) => <line key={y} x1="0" y1={y} x2="70" y2={y} stroke={GRID} />)}
      <polygon points="0,20 35,4 70,20" fill="#e3e9ef" stroke={GRID} />
      <text x="35" y="86" fontSize="8" fill={colors.textTertiary} textAnchor="middle">Detailed CAD</text>
    </g>
    <g transform="translate(120,38)"><path d="M0 6 H26 M20 0 l8 6 -8 6" stroke={ACCENT} strokeWidth="2" fill="none" /></g>
    {/* simplified */}
    <g transform="translate(166,8)">
      <rect x="0" y="20" width="70" height="52" fill={`${ACCENT}14`} stroke={ACCENT} strokeWidth="1.6" />
      <polygon points="0,20 35,4 70,20" fill={`${ACCENT}22`} stroke={ACCENT} strokeWidth="1.6" />
      <text x="35" y="86" fontSize="8" fill={ACCENT} textAnchor="middle" fontWeight="700">Clean massing</text>
    </g>
  </svg>
);

const DiaNorth = () => (
  <svg viewBox="0 0 120 90" width="100%" height="88" role="img" aria-label="Rotate model to true north">
    <circle cx="60" cy="46" r="30" fill="none" stroke={GRID} />
    <path d="M60 16 L67 46 L60 40 L53 46 Z" fill={BAD} />
    <path d="M60 76 L53 46 L60 52 L67 46 Z" fill={INK} />
    <text x="60" y="12" fontSize="9" fill={INK} textAnchor="middle" fontWeight="700">N</text>
    <text x="60" y="88" fontSize="8" fill={colors.textTertiary} textAnchor="middle">Project north = world north</text>
  </svg>
);

const DiaGround = () => (
  <svg viewBox="0 0 260 90" width="100%" height="88" role="img" aria-label="Sit the lowest level on the ground">
    {/* floating (bad) */}
    <g transform="translate(6,6)">
      <rect x="14" y="8" width="42" height="34" fill="#eef2f6" stroke={GRID} />
      <line x1="0" y1="64" x2="70" y2="64" stroke={INK} strokeWidth="1.5" />
      <path d="M35 42 V60" stroke={BAD} strokeWidth="1.4" strokeDasharray="3 3" />
      <XCircle x="52" y="0" width="14" height="14" color={BAD} />
      <text x="35" y="80" fontSize="8" fill={BAD} textAnchor="middle">floating / sunk</text>
    </g>
    {/* on ground (good) */}
    <g transform="translate(150,6)">
      <rect x="14" y="30" width="42" height="34" fill={`${ACCENT}14`} stroke={ACCENT} strokeWidth="1.6" />
      <line x1="0" y1="64" x2="70" y2="64" stroke={INK} strokeWidth="1.5" />
      <text x="35" y="80" fontSize="8" fill={GOOD} textAnchor="middle" fontWeight="700">base at Z = 0</text>
    </g>
  </svg>
);

const DiaGeom = () => (
  <svg viewBox="0 0 260 90" width="100%" height="88" role="img" aria-label="Closed clean geometry vs open soup">
    <g transform="translate(10,10)">
      <polygon points="10,50 40,50 50,30 20,30" fill={`${ACCENT}18`} stroke={ACCENT} strokeWidth="1.6" />
      <polygon points="10,50 10,64 40,64 40,50" fill={`${ACCENT}0d`} stroke={ACCENT} strokeWidth="1.6" />
      <polygon points="40,50 50,30 50,44 40,64" fill={`${ACCENT}14`} stroke={ACCENT} strokeWidth="1.6" />
      <text x="30" y="82" fontSize="8" fill={GOOD} textAnchor="middle" fontWeight="700">closed / watertight</text>
    </g>
    <g transform="translate(150,10)">
      <line x1="10" y1="50" x2="40" y2="50" stroke={BAD} strokeWidth="1.6" />
      <line x1="44" y1="52" x2="52" y2="30" stroke={BAD} strokeWidth="1.6" />
      <line x1="8" y1="30" x2="22" y2="30" stroke={BAD} strokeWidth="1.6" />
      <line x1="12" y1="64" x2="38" y2="62" stroke={BAD} strokeWidth="1.6" />
      <text x="30" y="82" fontSize="8" fill={BAD} textAnchor="middle">open / gappy soup</text>
    </g>
  </svg>
);

const Section = ({ icon: Icon, title, children, diagram }) => (
  <section style={s.section}>
    <div style={s.secHead}>
      <span style={s.secIcon}><Icon size={16} strokeWidth={2.1} /></span>
      <h3 style={s.secTitle}>{title}</h3>
    </div>
    <div style={s.secBody}>
      <div style={s.secText}>{children}</div>
      {diagram && <div style={s.secDia}>{diagram}</div>}
    </div>
  </section>
);

const Do = ({ children }) => (<li style={s.li}><CheckCircle2 size={13} color={GOOD} style={s.liIcon} /><span>{children}</span></li>);
const Dont = ({ children }) => (<li style={s.li}><XCircle size={13} color={BAD} style={s.liIcon} /><span>{children}</span></li>);

export default function ImportGuide({ open, onClose }) {
  if (!open) return null;
  return createPortal(
    <div style={s.overlay} data-testid="import-guide-modal" role="dialog" aria-modal="true" aria-label="Model import guide">
      <div style={s.card}>
        <button style={s.close} onClick={onClose} data-testid="import-guide-close" aria-label="Close guide"><X size={18} /></button>
        <div style={s.head}>
          <div style={s.kicker}>Best practices</div>
          <h2 style={s.title}>Prepare your model for a clean import</h2>
          <p style={s.lede}>A simplified model imports faster and lets the engine map roofs, façades and ground accurately. Simplify first, then follow these steps.</p>
        </div>

        <div style={s.scroll}>
          <Section icon={Scissors} title="1 · Simplify first" diagram={<DiaSimplify />}>
            <ul style={s.ul}>
              <Do>Keep the <strong>massing and major surfaces</strong>: roof planes, façade planes, floor slabs.</Do>
              <Dont>Strip furniture, mullions, railings, interior walls and fine construction detail.</Dont>
              <Dont>Avoid tiny fragmented faces and duplicated overlapping geometry.</Dont>
            </ul>
          </Section>

          <Section icon={Box} title="2 · Geometry type" diagram={<DiaGeom />}>
            <ul style={s.ul}>
              <Do>Prefer <strong>closed solids (BREP)</strong> or <strong>watertight meshes</strong> for building volumes.</Do>
              <Do>Single, flat <strong>surfaces are fine</strong> for individual roof/façade planes.</Do>
              <Dont>Avoid open, non-manifold &ldquo;surface soup&rdquo;, stray curves and points.</Dont>
              <Dont>No double-walls or coincident faces; they confuse surface detection.</Dont>
            </ul>
          </Section>

          <Section icon={Layers} title="3 · Level of detail (LOD)">
            <ul style={s.ul}>
              <Do>Aim for <strong>LOD 100–200</strong>: overall form + main surfaces.</Do>
              <Dont>Skip LOD 300–400 construction detail; it adds weight, not accuracy, for this tool.</Dont>
            </ul>
          </Section>

          <Section icon={Compass} title="4 · Orient to true north" diagram={<DiaNorth />}>
            <ul style={s.ul}>
              <Do>Rotate the project so <strong>model north points to real-world north</strong> before export.</Do>
              <Do>Fine-tune later with the in-app <strong>North offset</strong> if needed. Solar and DLI depend on it.</Do>
            </ul>
          </Section>

          <Section icon={MoveVertical} title="5 · Sit it on the ground" diagram={<DiaGround />}>
            <ul style={s.ul}>
              <Do>Place the <strong>lowest level flush with the ground plane at Z = 0</strong>.</Do>
              <Dont>Don&apos;t leave the model floating above or sunk below the origin; ground area reads wrong.</Dont>
            </ul>
          </Section>

          <Section icon={Layers} title="6 · Layer structure">
            <ul style={s.ul}>
              <Do>Organise layers by <strong>surface role</strong> so zones map cleanly, e.g. <em>Roof</em>, <em>Facade</em>, <em>Ground</em>, <em>Context</em> (neighbouring masses).</Do>
              <Do>Name layers clearly and put surrounding buildings on a <strong>Context</strong> layer so they shade but aren&apos;t counted.</Do>
            </ul>
          </Section>

          <Section icon={Ruler} title="7 · Units & scale">
            <ul style={s.ul}>
              <Do>Model at <strong>real-world scale in metres</strong>. A 30 m tower should measure 30 m.</Do>
              <Dont>Avoid millimetre or unitless files scaled by eye.</Dont>
            </ul>
          </Section>

          <Section icon={FileBox} title="8 · File formats">
            <ul style={s.ul}>
              <Do><strong>.3dm</strong> (Rhino: enable &ldquo;Save render meshes&rdquo;), <strong>.obj</strong>, <strong>.glb</strong>, <strong>.gltf</strong>.</Do>
              <Dont>.dwg can&apos;t be parsed in-browser; export to one of the above from your CAD tool.</Dont>
            </ul>
          </Section>
        </div>

        <div style={s.footer}>
          <button style={s.doneBtn} onClick={onClose} data-testid="import-guide-done">Back to upload</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const s = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,32,0.55)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 4200, padding: 20, boxSizing: "border-box" },
  card: { position: "relative", width: "min(760px, 96vw)", height: "88vh", background: "#fff", borderRadius: 20, boxShadow: "0 30px 90px rgba(0,0,0,0.32)", display: "flex", flexDirection: "column", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif", overflow: "hidden" },
  close: { position: "absolute", top: 14, right: 14, border: "none", background: "transparent", cursor: "pointer", color: colors.textTertiary, zIndex: 2 },
  head: { padding: "26px 28px 16px", borderBottom: `1px solid ${colors.border}` },
  kicker: { fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: ACCENT },
  title: { fontSize: 22, fontWeight: 800, color: colors.textPrimary, margin: "6px 0 6px" },
  lede: { fontSize: 13, lineHeight: 1.55, color: colors.textSecondary, margin: 0, maxWidth: 620 },
  scroll: { flex: 1, overflowY: "auto", padding: "8px 28px 12px" },
  section: { padding: "16px 0", borderBottom: `1px solid ${colors.border}` },
  secHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  secIcon: { width: 30, height: 30, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", background: `${ACCENT}14`, color: ACCENT, flexShrink: 0 },
  secTitle: { fontSize: 14.5, fontWeight: 800, color: colors.textPrimary, margin: 0 },
  secBody: { display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" },
  secText: { flex: "1 1 320px", minWidth: 260 },
  secDia: { flex: "0 0 260px", maxWidth: 260, background: "#f7f9fb", borderRadius: 12, border: `1px solid ${colors.border}`, padding: "8px 10px" },
  ul: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 },
  li: { display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, lineHeight: 1.5, color: colors.textSecondary },
  liIcon: { marginTop: 2, flexShrink: 0 },
  footer: { padding: "14px 28px", borderTop: `1px solid ${colors.border}`, display: "flex", justifyContent: "flex-end" },
  doneBtn: { padding: "11px 22px", borderRadius: 12, border: "none", background: ACCENT, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer", boxShadow: "0 6px 18px rgba(81,170,254,0.4)" },
};
